<template>
    <div>
        <template v-if="loaded">
            <rkm-alert v-if="isSaved && additionals.hasAvailableUpdate">
                Há movimentações disponíveis para registrar no livro.
            </rkm-alert>

            <rkm-form
                :show-legend="!isSaved"
                ref="Form"
            >
                <rkm-row>
                    <rkm-col
                        v-if="isSaved"
                        cols="2"
                    >
                        <rkm-form-input
                            disabled
                            label="Número"
                            v-model="model.numero"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-select
                            :disabled="isSaved || form.livrosRegistro.length === 1"
                            :options="form.livrosRegistro"
                            :validations="validations.livroRegistro"
                            @change="loadDataAberturaMinima"
                            label="Livro"
                            show-default-option
                            text-field="nome"
                            v-model="model.livroRegistroId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="2">
                        <rkm-form-select
                            :disabled="isSaved"
                            :options="form.tiposLivroRegistro"
                            :validations="validations.livroRegistroTipo"
                            @change="loadDataAberturaMinima"
                            label="Tipo"
                            show-default-option
                            text-field="nome"
                            v-model="model.livroRegistroTipoId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-date-picker
                            :disabled="isSaved || !dataMinimaMaximaAberturaLoaded"
                            :max-date="dataMaximaAbertura"
                            :min-date="dataMinimaAbertura"
                            :validations="validations.abertoEm"
                            label="Data de abertura"
                            v-model="model.abertoEm"
                        />
                    </rkm-col>

                    <rkm-col :cols="isSaved ? 2 : 3">
                        <rkm-form-input
                            :disabled="isSaved"
                            :validations="validations.quantidadePaginas"
                            label="Quantidade de páginas"
                            type="number"
                            v-model="model.quantidadePaginas"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-area
                    :class="{ 'pb-0': isSaved && !termoAberturaVisible }"
                    no-margin-top
                    title="Termo de abertura"
                    type="fieldset"
                >
                    <template slot="trailing">
                        <rkm-button
                            v-if="isSaved"
                            :title="termoAberturaVisible ? 'Ocultar' : 'Exibir'"
                            @click="termoAberturaVisible = !termoAberturaVisible"
                            color="primary"
                            small
                        >
                            <rkm-icon :icon="`angle-double-${termoAberturaVisible ? 'up' : 'down'}`" />
                        </rkm-button>
                    </template>

                    <component
                        :is="isSaved ? 'rkm-collapse' : 'div'"
                        :visible="termoAberturaVisible"
                    >
                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input
                                    :disabled="isSaved"
                                    :validations="validations.registro"
                                    label="Registro"
                                    uppercase
                                    v-model="model.registro"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input
                                    disabled
                                    label="Da firma"
                                    uppercase
                                    v-model="form.firma"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input
                                    disabled
                                    label="Farmácia"
                                    uppercase
                                    v-model="form.farmacia"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input-autocomplete
                                    :disabled="isSaved"
                                    :repository="repositories.usuario"
                                    :search-fields="autocompleteFields.usuarioFarmaceutico"
                                    :validations="validations.usuarioFarmaceutico"
                                    label="Farmacêutico"
                                    text-field="cidadaoNome"
                                    uppercase
                                    v-model="model.usuarioFarmaceutico"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-input
                                    disabled
                                    label="Estabelecido à"
                                    uppercase
                                    v-model="form.endereco"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col cols="3">
                                <rkm-form-input
                                    :disabled="isSaved"
                                    :validations="validations.inscricaoEstadual"
                                    label="Inscrição estadual"
                                    number
                                    v-model="model.inscricaoEstadual"
                                />
                            </rkm-col>

                            <rkm-col>
                                <rkm-form-input
                                    :disabled="isSaved"
                                    :validations="validations.autoridadeSanitaria"
                                    label="Autoridade sanitária"
                                    uppercase
                                    v-model="model.autoridadeSanitaria"
                                />
                            </rkm-col>
                        </rkm-row>
                    </component>
                </rkm-area>
            </rkm-form>

            <template v-if="isSaved">
                <rkm-table
                    :fields="tableFields.pagina"
                    :items="model.paginas"
                    class="mt-2"
                    sort-by="numero"
                >
                    <template v-slot:cell(actions)="{ item }">
                        <rkm-button
                            @click="$refs.ModalPagina.open(item)"
                            color="primary"
                            small
                            title="Visualizar"
                        >
                            <rkm-icon icon="eye" />
                        </rkm-button>
                    </template>
                </rkm-table>

                <ModalPagina
                    :repository="repository"
                    ref="ModalPagina"
                    :livro-registro-id="model.livroRegistroId"
                    :livro-registro-tipo-id="model.livroRegistroTipoId"
                />
            </template>
        </template>
    </div>
</template>

<script>
// Components.
import ModalPagina from './components/pagina/Modal'

// Others.
import { mapActions } from 'vuex'
import { repository, error, stringCase, object, data, file } from '@/mixins'
import moment from 'moment'
import livroStatus from '@/enums/farmacia/livroStatus'
import livroRegistro from '@/enums/cadastro/material/livroRegistro'

export default {
    components: { ModalPagina },
    computed: {
        isLivroPsicotropico() {

            return this.funcionalidade === 'livro-psicotropico'
        },
        isLivroTalidomida() {

            return this.funcionalidade === 'livro-talidomida'
        },
        isLivroAntimicrobiandoSelecionado() {

            return this.model.livroRegistroId === this.enums.livroRegistro.ANTIMICROBIANOS
        },
        repository() {

            if (this.isLivroPsicotropico) {

                return this.repositories.livroPsicotropico
            }
            else if (this.isLivroTalidomida) {

                return this.repositories.livroTalidomida
            }

            return null
        },
        isSaved() {

            return !!this.id
        },
        validations() {

            return {
                abertoEm: { required: true },
                livroRegistro: { required: true },
                livroRegistroTipo: { required: !this.isLivroAntimicrobiandoSelecionado },
                quantidadePaginas: { required: true, min_value: 1, max_value: 999 },
                registro: { required: true, max: 100 },
                usuarioFarmaceutico: { required: true },
                inscricaoEstadual: { min: 8, max: 14 },
                autoridadeSanitaria: { required: true, max: 100 },
            }
        },
        autocompleteFields() {

            return {
                usuarioFarmaceutico: [
                    { key: 'cadastroCidadao.nome', searchable: true },
                ],
            }
        },
        tableFields() {

            return {
                pagina: [
                    {
                        key: 'numero',
                        label: 'Página',
                        sortable: true,
                        class: 'col-min text-center'
                    },
                    {
                        key: 'material',
                        label: 'Medicamento',
                        sortable: true,
                    },
                    {
                        key: 'actions',
                        label: 'Ações',
                        class: 'col-min text-center'
                    },
                ]
            }
        }
    },
    created() {

        this.repositories = {
            usuario: this.getConectaSusRepository('cadastro.sujeitoAtencao.usuario'),
            livroPsicotropico: this.getConectaSusRepository('farmacia.livroPsicotropico'),
            livroTalidomida: this.getConectaSusRepository('farmacia.livroTalidomida'),
            impressao: this.getConectaSusRepository('impressao.farmacia.livro')
        }

        this.load()
    },
    data: () => ({
        additionals: null,
        form: null,
        loaded: false,
        model: null,
        termoAberturaVisible: false,
        dataMinimaMaximaAberturaLoaded: false,
        dataMinimaAbertura: null,
        dataMaximaAbertura: null,
        enums: {
            livroStatus,
            livroRegistro,
        },
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            const request = this.id
                ? this.repository.edit(this.id)
                : this.repository.create()

            request
                .then(({ data }) => {

                    if (data.model.abertoEm) {

                        data.model.abertoEm = moment(data.model.abertoEm, 'DD/MM/YYYY').toDate()
                    }

                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    if (!this.isSaved && this.form.livrosRegistro.length === 1) {

                        this.model.livroRegistroId = this.form.livrosRegistro[0].id
                    }

                    this.$emit('data-load', {
                        additionals: this.additionals,
                        model: this.model,
                    })

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        save() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.setLoading()

                    this.repository.store(this.prepareData())
                        .then(({ data }) => {

                            this.showSaveNotification()
                            this.$emit('save', data)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData() {

            const data = this.clone(this.model)

            this.createIdKeysToObject(data, ['usuarioFarmaceutico'])

            this.formateDates(data, [
                'abertoEm',
            ], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        fecharLivro() {

            const fecharLivro = () => {

                this.setLoading()

                this.repository.fecharLivro(this.id)
                    .then(({ data }) => {

                        const finish = async () => this.$emit('save', data)

                        this.showConfirmation("Deseja imprimir o termo de encerramento?",
                            async () => this.printTermoFechamento(finish),
                            finish
                        )
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }

            const dataAbertura = moment(this.model.abertoEm).format('DD/MM/YYYY')

            this.showConfirmation(
                `Após fechar o livro aberto em ${dataAbertura}, o mesmo não poderá ser aberto novamente.
                Deseja continuar?`,
                async () => fecharLivro()
            )
        },
        print() {

            const handlePrint = async () => {

                this.setLoading()

                this.repositories.impressao.livro(this.id)
                    .then(response => {

                        this.openFile(response.data, response.headers['content-type'])
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }

            if (this.model.statusId === this.enums.livroStatus.ABERTO) {

                this.showConfirmation("O livro ainda está aberto, deseja imprimir parcialmente?", handlePrint)
            }
            else {

                handlePrint()
            }
        },
        printTermoFechamento(callback) {

            this.setLoading()

            this.repositories.impressao.termoFechamento(this.id)
                .then(response => {

                    this.openFile(response.data, response.headers['content-type'])

                    if (typeof callback === 'function') {

                        callback()
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadDataAberturaMinima() {

            this.model.abertoEm = null

            if ((this.model.livroRegistroId !== null && this.model.livroRegistroTipoId !== null)
                || (this.isLivroAntimicrobiandoSelecionado && this.model.livroRegistroTipoId === null)
            ) {

                this.setLoading()

                this.repository.getDataAbertura(this.model.livroRegistroId, this.model.livroRegistroTipoId)
                    .then(({ data }) => {

                        this.dataMinimaAbertura = data.dataMinima
                        this.dataMaximaAbertura = data.dataMaxima
                        this.dataMinimaMaximaAberturaLoaded = true
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
            else {

                this.dataMinimaAbertura = null
                this.dataMaximaAbertura = null
                this.dataMinimaMaximaAberturaLoaded = false
            }
        },
        updateMovimentacoes() {

            this.showConfirmation("Deseja realmente atualizar as movimentações?",
                async () => {

                    this.setLoading()

                    this.repository.update(this.id)
                        .then(() => {

                            this.showSaveNotification()
                            this.load()
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                }
            )
        },
    },
    mixins: [repository, error, stringCase, object, data, file],
    props: {
        id: { type: Number },
        funcionalidade: { type: String, required: true },
    },
}
</script>